/**
 * Recovery for stale dynamic-import (code-split chunk) failures.
 *
 * The app is code-split: routes and heavy utilities (e.g. the proposal PDF
 * generator) are loaded on demand as hashed chunk files. After a new deploy,
 * an already-open browser tab still references the OLD chunk hashes. Those
 * files no longer exist on the server, so Firebase Hosting serves the SPA
 * fallback (index.html) for the missing `.js`. The browser then fails to parse
 * HTML as a module — surfacing as:
 *
 *   "Failed to fetch dynamically imported module: …/assets/<name>-<hash>.js"
 *
 * The only recovery is to reload so the browser fetches the current index.html
 * and its up-to-date chunk hashes. Instead of an error page, we show a branded
 * "Live update in progress" overlay and reload automatically. A sessionStorage
 * guard prevents a genuinely broken (not merely stale) chunk from causing an
 * infinite reload loop.
 */

import { showLiveUpdateOverlay, hideLiveUpdateOverlay } from './liveUpdateOverlay';

const RELOAD_GUARD_KEY = 'chunkReloadAttemptedAt';
const RELOAD_GUARD_WINDOW_MS = 30_000;
// Long enough for the user to read "Live update in progress" instead of an
// unexplained flash-reload; short enough to feel instant.
const OVERLAY_DWELL_MS = 900;

/**
 * True when the error looks like a failed dynamic import / module preload —
 * i.e. a stale chunk after a deploy, across Chrome/Firefox/Safari wording.
 */
export function isDynamicImportError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module|loading chunk [\w-]+ failed|loading css chunk|chunkloaderror|expected a javascript.?(?:-or-wasm)?.? module script but the server responded with a mime type/i.test(
    msg
  );
}

/**
 * Reload once to pick up the latest deploy. Returns true if a reload was
 * triggered, false if suppressed by the guard (so the caller can surface a
 * gentle manual-update UI instead of looping).
 */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (last && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false; // already reloaded very recently — avoid a loop
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — fall through and reload.
  }
  window.location.reload();
  return true;
}

let recovering = false;

/**
 * The full self-heal: show the "Live update in progress" overlay, then reload
 * (guarded). Returns false when the guard suppressed the reload — the caller
 * should then show a manual "Update now" UI rather than an error page.
 */
export function recoverFromStaleChunk(): boolean {
  if (recovering) return true;

  // Peek at the guard BEFORE showing the overlay, so a guard-suppressed call
  // doesn't flash an overlay it can't follow through on.
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (last && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
  } catch {
    // ignore — reloadForStaleChunk handles storage failures itself
  }

  recovering = true;
  showLiveUpdateOverlay();
  window.setTimeout(() => {
    if (!reloadForStaleChunk()) {
      recovering = false;
      hideLiveUpdateOverlay();
    }
  }, OVERLAY_DWELL_MS);
  return true;
}

/**
 * Install the global handlers for stale-chunk failures:
 *  - Vite's `vite:preloadError` (fired for failed module preloads/imports)
 *  - `unhandledrejection` (belt & braces: import() rejections that bypass the
 *    Vite event, e.g. some Safari paths and manual dynamic imports)
 * Both route through the overlay + guarded reload so a stale tab silently
 * upgrades to the current deploy instead of dead-ending on an error page.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Stop Vite from re-throwing it as an uncaught error / Sentry noise — we
    // are handling it by reloading.
    event.preventDefault();
    recoverFromStaleChunk();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isDynamicImportError(event.reason)) {
      event.preventDefault();
      recoverFromStaleChunk();
    }
  });
}
